import * as Sentry from '@sentry/react';
import React, { PureComponent } from 'react';
import { ERROR_MESSAGES } from './errors';
import { toaster } from '@/utils/toaster';
/**
 * Base/Abstract Class for Error Boundary implementation. This boundary should not be used
 * directly and instead, should be extended during implementation.
 *
 * @export
 * @class BaseErrorBoundary
 * @extends {PureComponent}
 */
export class BaseErrorBoundary extends PureComponent {
  // Decorator / HOC Pattern. This static member will wrap the desired component in a boundary
  static withBoundary() {
    /** Note, "this" is of the caller.
     * Since this class is always extended you should **almost** NEVER call
     * BaseErrorBoundary.withBoundary */
    const Boundary = this; // Assigned this to boundary for clarity instead of <this..
    return (WrappedComponent) => (props) => (
      <Boundary {...props}>
        <WrappedComponent {...props} />
      </Boundary>
    );
  }

  // Called immediately by React to this.setState({ stateOnError }) during an error
  static getDerivedStateFromError(err) {
    return {
      hasError: true,
      errorType: err.message,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorType: null,
    };
  }

  /**
   * Wrapper for rsuite Alert so you can define custom alerts with the Error Boundary
   * and not have to import { Alert } from ...
   */
  // eslint-disable-next-line class-methods-use-this
  alert(error, alertType, duration = 2000) {
    // allows mapping to predefined message or custom
    const messageToDisplay = ERROR_MESSAGES[error] || error;
    toaster[alertType](messageToDisplay, duration);
  }

  /* eslint-disable class-methods-use-this */
  componentDidCatch(err) {
    // TODO: We may need to check if we are in development to see errors in console
    toaster.error(ERROR_MESSAGES.DefaultException);
    // hopefully reports all caught errors to Sentry
    Sentry.captureException(err);
  }

  /**
   * @abstract Child classes should provide their own render
   */
  // eslint-disable-next-line react/require-render-return
  render() {
    throw new Error('Abstract method, override render required');
  }
}
